import { API_BASE_URL } from '../config/constants';

// API Configuration
export const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
};

// API Response interfaces based on backend responses
export interface ApiResponse<T> {
  data?: T;
  message?: string;
  detail?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface LoginResponse {
  user: {
    id: string;
    email: string;
    full_name: string;
    role: string;
    is_verified: boolean;
    customer_id: string | null;
    customer_profile?: {
      id: string;
      epicor_customer_id: string | null;
      company_name: string;
      contact_person: string | null;
      phone: string | null;
      address: string | null;
      credit_limit: number;
      outstanding_balance: number;
      available_credit: number;
      customer_group: string | null;
      price_list_code: string | null;
      is_approved: boolean;
      last_sync_at: string | null;
      credit_utilization_percentage: number;
    };
  };
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
  message: string;
}

export interface RegisterResponse {
  message: string;
  detail: string;
  user_id: string;
}

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: string;
  epicor_customer_id: string | null;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductResponse {
  id: string;
  epicor_part_number: string;
  product_code: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  family_code?: string;
  family_name?: string;
  subfamily_code?: string;
  subfamily_name?: string;
  brand_code?: string;
  brand_name?: string;
  category_item_code?: string;
  category_item_name?: string;
  unit_of_measure: string | null;
  is_active: boolean;
  image_url: string | null;
  created_at: string;
  last_sync_at: string;
}

export interface ProductWithPricing extends ProductResponse {
  unit_price?: number | null;
  discount_percent?: number | null;
  discounted_price?: number | null;
  promotion_discount?: number | null;
  price_effective_from?: string | null;
  price_effective_to?: string | null;
  msds_url?: string | null;
}

export interface CustomerCatalogResponse {
  products: ProductWithPricing[];
  total_count: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

export interface GroupedCatalogResponse {
  groups: Array<{
    group_key: string;
    brand_code?: string;
    brand_name?: string;
    category_item_code?: string;
    category_item_name?: string;
    subfamily_code?: string;
    subfamily_name?: string;
    family_code?: string;
    family_name?: string;
    sales_uom?: string;
    category_label?: string;
    product_name?: string;
    finish_type?: string;
    representative_image_url?: string | null;
    variant_count: number;
    min_price?: number | null;
    max_price?: number | null;
    color_swatches: Array<{ color_name?: string; color_hex?: string; color_image_url?: string }>;
    promotion_discount?: number | null;
  }>;
  total_count: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

// Base API client class
export class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.defaultHeaders = API_CONFIG.headers;
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    // Lazy import to avoid circular dependency
    const { authService } = await import('./authService');
    try {
      // Mutex: reuse in-flight refresh
      if (!this.refreshPromise) {
        this.refreshPromise = authService.refreshToken().then(() => {});
      }
      await this.refreshPromise;
      return true;
    } catch {
      authService.logout();
      window.location.href = '/login';
      return false;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      // Auto-retry on 401 (skip for auth endpoints and retries)
      if (
        response.status === 401 &&
        !isRetry &&
        !endpoint.startsWith('/auth/')
      ) {
        const refreshed = await this.attemptTokenRefresh();
        if (refreshed) {
          // Rebuild headers with new token
          const newToken = localStorage.getItem('auth_token');
          if (newToken && config.headers) {
            (config.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
          }
          return this.request<T>(endpoint, { ...options, headers: config.headers }, true);
        }
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // Public HTTP methods
  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      headers,
    });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers,
    });
  }

  async put<T>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers,
    });
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      headers,
    });
  }

  async delete<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      headers,
    });
  }
}

// Create a singleton instance
export const apiClient = new ApiClient();

// Helper function to get auth headers
export const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to get backoffice auth headers
export const getBackofficeAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('backoffice_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to handle API errors
export const handleApiError = (error: any): string => {
  if (error.message) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
};

// Enhanced error handler for payment operations
export const handlePaymentError = (error: any): string => {
  // Handle specific payment error messages
  const errorMessage = error.message || error.detail || error;
  
  if (typeof errorMessage === 'string') {
    // Common payment error scenarios
    if (errorMessage.toLowerCase().includes('order not found')) {
      return 'Invoice tidak memiliki order yang terkait. Silakan hubungi customer service.';
    }
    
    if (errorMessage.toLowerCase().includes('invoice not found')) {
      return 'Invoice tidak ditemukan. Silakan refresh halaman dan coba lagi.';
    }
    
    if (errorMessage.toLowerCase().includes('access denied')) {
      return 'Anda tidak memiliki akses ke invoice ini.';
    }
    
    if (errorMessage.toLowerCase().includes('already paid')) {
      return 'Invoice ini sudah dibayar.';
    }
    
    if (errorMessage.toLowerCase().includes('not posted')) {
      return 'Invoice belum dapat dibayar. Status invoice harus POSTED.';
    }
    
    if (errorMessage.toLowerCase().includes('payment service')) {
      return 'Layanan pembayaran sedang tidak tersedia. Silakan coba beberapa saat lagi.';
    }
    
    if (errorMessage.toLowerCase().includes('midtrans')) {
      return 'Terjadi masalah dengan sistem pembayaran. Silakan coba lagi.';
    }
    
    if (errorMessage.toLowerCase().includes('ssl') || errorMessage.toLowerCase().includes('certificate')) {
      return 'Masalah koneksi ke sistem pembayaran. Silakan coba lagi.';
    }
    
    if (errorMessage.toLowerCase().includes('timeout')) {
      return 'Koneksi timeout. Silakan coba lagi.';
    }
    
    // Return the original message if no specific case matches
    return errorMessage;
  }
  
  return 'Terjadi kesalahan saat memproses pembayaran. Silakan coba lagi.';
};